import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, ArrowLeft, UserPlus, Trash2, Edit, Shield, Headset, PenTool, KeyRound, Camera, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { normalizeRole, getAssignableRoles } from '@/lib/rbac';
import { generatePattern, getDefaultMeasurements, resolveGeometryVersion, resolveFormulaVersion } from '@/lib/drafting';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const ROLE_ICONS = {
  owner: Shield,
  admin: Users,
  patternmaker: PenTool,
  support: Headset,
};

const ROLE_COLORS = {
  owner: 'bg-purple-100 text-purple-800 border-purple-200',
  admin: 'bg-blue-100 text-blue-800 border-blue-200',
  patternmaker: 'bg-green-100 text-green-800 border-green-200',
  support: 'bg-gray-100 text-gray-800 border-gray-200'
};

const ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Admin',
  patternmaker: 'Patternmaker',
  support: 'Support',
};

const SNAPSHOT_RENDER_KEYS = ['frontPath', 'front_path', 'sidePath', 'side_path', 'backPath', 'back_path', 'yokePath', 'yoke_path', 'sleevePath', 'sleeve_path', 'topSleevePath', 'top_sleeve_path', 'underSleevePath', 'under_sleeve_path'];
const SNAPSHOT_SUPPORTED_GARMENTS = ['trouser', 'shirt', 'jacket'];
const SNAPSHOT_BATCH_SIZE = 12;
const SNAPSHOT_SOURCE = 'batch_legacy_freeze';

const hasRenderablePatternSnapshot = (patternData) => Boolean(
  patternData
  && typeof patternData === 'object'
  && SNAPSHOT_RENDER_KEYS.some((key) => Boolean(patternData[key]))
);

const roundToSixteenth = (value) => Math.round(value / 0.0625) * 0.0625;

const normalizeJacketPointDeltaMap = (rawPointDeltas = {}) => Object.entries(rawPointDeltas || {}).reduce((acc, [pointName, delta]) => {
  if (!pointName || !delta || typeof delta !== 'object') return acc;
  const x = roundToSixteenth(Number(delta.x) || 0);
  const y = roundToSixteenth(Number(delta.y) || 0);
  if (Math.abs(x) < 1e-8 && Math.abs(y) < 1e-8) return acc;
  acc[pointName] = { x, y };
  return acc;
}, {});

const combineJacketPointDeltaMaps = (baseMap = {}, deltaMap = {}) => {
  const base = normalizeJacketPointDeltaMap(baseMap);
  const delta = normalizeJacketPointDeltaMap(deltaMap);
  const combined = { ...base };
  Object.entries(delta).forEach(([pointName, pointDelta]) => {
    const existing = combined[pointName] || { x: 0, y: 0 };
    combined[pointName] = {
      x: roundToSixteenth((Number(existing.x) || 0) + (Number(pointDelta?.x) || 0)),
      y: roundToSixteenth((Number(existing.y) || 0) + (Number(pointDelta?.y) || 0)),
    };
  });
  return normalizeJacketPointDeltaMap(combined);
};

const parseJacketPointDeltasPayload = (pointDeltasPayload = {}) => {
  const payload = pointDeltasPayload && typeof pointDeltasPayload === 'object' ? pointDeltasPayload : {};
  const hasStructuredPayload = Object.prototype.hasOwnProperty.call(payload, 'deltas')
    || Object.prototype.hasOwnProperty.call(payload, 'baseline_deltas')
    || Object.prototype.hasOwnProperty.call(payload, 'mode');

  const deltas = normalizeJacketPointDeltaMap(hasStructuredPayload ? payload.deltas : payload);
  const baselineDeltas = normalizeJacketPointDeltaMap(payload.baseline_deltas || {});
  const mode = String(payload.mode || 'fixed').toLowerCase() === 'free' ? 'free' : 'fixed';

  return { mode, deltas, baselineDeltas };
};

const resolveEffectiveJacketPointDeltas = ({ mode = 'fixed', deltas = {}, baselineDeltas = {} } = {}) => {
  if (mode === 'free') {
    return combineJacketPointDeltaMaps(baselineDeltas, deltas);
  }
  return normalizeJacketPointDeltaMap(baselineDeltas);
};

const buildSnapshotAuditRecord = (pattern) => {
  const garmentType = String(pattern?.garment_type || '').toLowerCase();
  const snapshot = pattern?.pattern_data;
  if (hasRenderablePatternSnapshot(snapshot)) {
    return {
      status: 'already-frozen',
      reason: 'Renderable snapshot already exists',
      garmentType,
      formulaVersion: pattern?.formula_version || pattern?.pattern_data?.formulaVersion || null,
      geometryVersion: pattern?.geometry_version || pattern?.pattern_data?.geometryVersion || null,
    };
  }

  if (!SNAPSHOT_SUPPORTED_GARMENTS.includes(garmentType)) {
    return {
      status: 'skipped',
      reason: 'Garment is not in the supported freeze set',
      garmentType,
      formulaVersion: pattern?.formula_version || null,
      geometryVersion: pattern?.geometry_version || null,
    };
  }

  const measurements = pattern?.measurements;
  if (!measurements || typeof measurements !== 'object' || Object.keys(measurements).length === 0) {
    return {
      status: 'skipped',
      reason: 'Missing saved measurements',
      garmentType,
      formulaVersion: pattern?.formula_version || null,
      geometryVersion: pattern?.geometry_version || null,
    };
  }

  const formulaVersion = pattern?.formula_version || pattern?.pattern_data?.formulaVersion || null;
  const geometryVersion = pattern?.geometry_version || pattern?.pattern_data?.geometryVersion || null;
  if (!formulaVersion && !geometryVersion) {
    return {
      status: 'skipped',
      reason: 'Missing explicit formula/geometry version metadata',
      garmentType,
      formulaVersion,
      geometryVersion,
    };
  }

  return {
    status: 'eligible',
    reason: 'Missing renderable snapshot',
    garmentType,
    formulaVersion,
    geometryVersion,
  };
};

const chunkArray = (items = [], chunkSize = SNAPSHOT_BATCH_SIZE) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
};

export default function AdminPanel({ onBack }) {
  const { user: currentUser, token } = useAuth();
  const currentRole = normalizeRole(currentUser?.role);
  const assignableRoles = getAssignableRoles(currentRole);
  const canManageLegacySnapshots = ['owner', 'admin'].includes(currentRole);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotAudit, setSnapshotAudit] = useState(null);
  const [snapshotScopeClientNumber, setSnapshotScopeClientNumber] = useState('');
  const [snapshotScopeGarment, setSnapshotScopeGarment] = useState('all');
  const [snapshotProgress, setSnapshotProgress] = useState({
    phase: 'idle',
    processed: 0,
    total: 0,
    currentPatternNumber: '',
  });
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'support'
  });
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    role: 'support'
  });
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    password: '',
    confirmPassword: '',
  });

  const fetchPatternList = async () => {
    const params = new URLSearchParams();
    if (snapshotScopeClientNumber.trim()) {
      params.set('client_number', snapshotScopeClientNumber.trim().toUpperCase());
    }
    if (snapshotScopeGarment !== 'all') {
      params.set('garment_type', snapshotScopeGarment);
    }

    const url = `${API_BASE}/api/patterns${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.detail || 'Failed to load patterns for snapshot audit');
    }
    return Array.isArray(payload) ? payload : [];
  };

  const fetchPatternDetail = async (patternId) => {
    const response = await fetch(`${API_BASE}/api/patterns/${patternId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.detail || 'Failed to load pattern details');
    }
    return payload;
  };

  const buildFrozenPatternPayload = (pattern) => {
    const garmentType = String(pattern?.garment_type || 'trouser').toLowerCase();
    const baseMeasurements = getDefaultMeasurements(garmentType);
    const measurements = {
      ...baseMeasurements,
      ...((pattern?.measurements && typeof pattern.measurements === 'object') ? pattern.measurements : {}),
    };
    const geometryVersion = resolveGeometryVersion(garmentType, pattern || {});
    const formulaVersion = resolveFormulaVersion(garmentType, {
      ...(pattern || {}),
      geometry_version: geometryVersion,
    });
    const parsedJacketPointDeltas = garmentType === 'jacket'
      ? parseJacketPointDeltasPayload(pattern?.point_deltas || {})
      : { mode: 'fixed', deltas: {}, baselineDeltas: {} };
    const generatedSnapshot = generatePattern(
      measurements,
      {
        backCrotchScoopX: measurements.backCrotchScoopX,
        backCrotchScoopY: measurements.backCrotchScoopY,
        formulaVersion,
        geometryVersion,
        freeModeActive: garmentType === 'jacket' && parsedJacketPointDeltas.mode === 'free',
        pointDeltas: garmentType === 'jacket'
          ? resolveEffectiveJacketPointDeltas(parsedJacketPointDeltas)
          : {},
      },
      garmentType,
    );

    return {
      pattern_number: pattern.pattern_number,
      client_number: pattern.client_number,
      client_name: pattern.client_name,
      garment_type: pattern.garment_type,
      measurements,
      parameters: {
        ...((pattern?.parameters && typeof pattern.parameters === 'object') ? pattern.parameters : {}),
        snapshot_frozen_at: new Date().toISOString(),
        snapshot_frozen_by: currentUser?.email || currentUser?.id || 'unknown',
        snapshot_source: SNAPSHOT_SOURCE,
      },
      pattern_data: {
        ...generatedSnapshot,
        garmentType,
        formulaVersion,
        geometryVersion,
      },
      export_layout: pattern?.export_layout || null,
      point_deltas: pattern?.point_deltas || null,
      formula_version: formulaVersion,
      geometry_version: geometryVersion,
      notes: pattern?.notes || null,
    };
  };

  const runSnapshotAudit = async () => {
    if (!canManageLegacySnapshots) return;

    setSnapshotLoading(true);
    setError('');
    setSuccess('');
    setSnapshotProgress({ phase: 'audit', processed: 0, total: 0, currentPatternNumber: '' });

    try {
      const patternSummaries = await fetchPatternList();
      const detailBatches = chunkArray(patternSummaries, SNAPSHOT_BATCH_SIZE);
      const detailedPatterns = [];

      for (const batch of detailBatches) {
        const batchDetails = await Promise.all(batch.map((pattern) => fetchPatternDetail(pattern.id)));
        detailedPatterns.push(...batchDetails);
        setSnapshotProgress((prev) => ({
          ...prev,
          processed: detailedPatterns.length,
          total: patternSummaries.length,
          currentPatternNumber: batchDetails[batchDetails.length - 1]?.pattern_number || '',
        }));
      }

      const records = detailedPatterns.map((pattern) => {
        const classification = buildSnapshotAuditRecord(pattern);
        return {
          id: pattern.id,
          patternNumber: pattern.pattern_number,
          clientNumber: pattern.client_number,
          clientName: pattern.client_name,
          garmentType: classification.garmentType,
          formulaVersion: classification.formulaVersion,
          geometryVersion: classification.geometryVersion,
          status: classification.status,
          reason: classification.reason,
        };
      });

      const summary = records.reduce((acc, record) => {
        acc.total += 1;
        acc[record.status] = (acc[record.status] || 0) + 1;
        acc.byGarment[record.garmentType] = acc.byGarment[record.garmentType] || { total: 0, eligible: 0, alreadyFrozen: 0, skipped: 0 };
        acc.byGarment[record.garmentType].total += 1;
        if (record.status === 'eligible') acc.byGarment[record.garmentType].eligible += 1;
        if (record.status === 'already-frozen') acc.byGarment[record.garmentType].alreadyFrozen += 1;
        if (record.status === 'skipped') acc.byGarment[record.garmentType].skipped += 1;
        return acc;
      }, { total: 0, eligible: 0, 'already-frozen': 0, skipped: 0, byGarment: {} });

      setSnapshotAudit({
        records,
        summary,
        generatedAt: new Date().toISOString(),
        scope: {
          clientNumber: snapshotScopeClientNumber.trim().toUpperCase(),
          garmentType: snapshotScopeGarment,
        },
      });
      setSnapshotProgress({ phase: 'idle', processed: records.length, total: records.length, currentPatternNumber: '' });
      setSuccess(`Snapshot audit complete. ${summary.eligible || 0} patterns are ready to freeze.`);
    } catch (auditError) {
      setError(auditError.message || 'Snapshot audit failed');
      setSnapshotProgress({ phase: 'idle', processed: 0, total: 0, currentPatternNumber: '' });
    } finally {
      setSnapshotLoading(false);
    }
  };

  const runSnapshotFreeze = async () => {
    if (!canManageLegacySnapshots) return;
    if (!snapshotAudit?.summary?.eligible) {
      setError('Run the snapshot audit first.');
      return;
    }

    setSnapshotLoading(true);
    setError('');
    setSuccess('');

    try {
      const eligibleRecords = snapshotAudit.records.filter((record) => record.status === 'eligible');
      const results = [];
      setSnapshotProgress({ phase: 'freeze', processed: 0, total: eligibleRecords.length, currentPatternNumber: '' });

      for (const record of eligibleRecords) {
        try {
          const fullPattern = await fetchPatternDetail(record.id);
          const frozenPayload = buildFrozenPatternPayload(fullPattern);
          const response = await fetch(`${API_BASE}/api/patterns/${record.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(frozenPayload),
          });
          const payload = await response.json();
          if (!response.ok) {
            throw new Error(payload.detail || 'Failed to freeze pattern snapshot');
          }
          results.push({ ...record, status: 'frozen', reason: 'Snapshot frozen successfully' });
        } catch (freezeError) {
          results.push({ ...record, status: 'failed', reason: freezeError.message || 'Snapshot freeze failed' });
        }

        setSnapshotProgress((prev) => ({
          ...prev,
          processed: prev.processed + 1,
          total: eligibleRecords.length,
          currentPatternNumber: record.patternNumber,
        }));
      }

      const frozenCount = results.filter((record) => record.status === 'frozen').length;
      const failedCount = results.filter((record) => record.status === 'failed').length;
      toast.success(`Legacy snapshot freeze finished. ${frozenCount} frozen${failedCount ? `, ${failedCount} failed` : ''}.`);
      setSuccess(`Legacy snapshot freeze finished. ${frozenCount} frozen${failedCount ? `, ${failedCount} failed` : ''}.`);
      await runSnapshotAudit();
    } catch (freezeError) {
      setError(freezeError.message || 'Snapshot freeze failed');
    } finally {
      setSnapshotLoading(false);
      setSnapshotProgress((prev) => ({ ...prev, phase: 'idle', currentPatternNumber: '' }));
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (assignableRoles.length === 0) return;
    if (!assignableRoles.includes(createForm.role)) {
      setCreateForm((prev) => ({ ...prev, role: assignableRoles[0] }));
    }
  }, [assignableRoles, createForm.role]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setUsers((data || []).map((row) => ({ ...row, role: normalizeRole(row.role) })));
      } else {
        setError(data.detail || 'Failed to load users');
      }
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      const res = await fetch(`${API_BASE}/api/auth/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(createForm)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create user');
      }
      
      setSuccess(`User ${data.email} created successfully!`);
      setCreateDialogOpen(false);
      setCreateForm({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        role: assignableRoles[0] || 'support'
      });
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      const res = await fetch(`${API_BASE}/api/auth/admin/users/${editUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to update user');
      }
      
      setSuccess(`User ${data.email} updated successfully!`);
      setEditDialogOpen(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      const res = await fetch(`${API_BASE}/api/auth/admin/users/${deleteUser.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to delete user');
      }
      
      setSuccess(`User ${deleteUser.email} deleted successfully!`);
      setDeleteDialogOpen(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    setEditForm({
      first_name: user.first_name,
      last_name: user.last_name,
      role: normalizeRole(user.role)
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setDeleteUser(user);
    setDeleteDialogOpen(true);
  };

  const openResetDialog = (user) => {
    setResetUser(user);
    setResetPasswordForm({ password: '', confirmPassword: '' });
    setResetDialogOpen(true);
  };

  const canEditUser = (user) => {
    const targetRole = normalizeRole(user.role);
    if (currentRole === 'owner') return true;
    if (currentRole === 'admin') return targetRole !== 'owner' && targetRole !== 'admin';
    return false;
  };

  const canDeleteUser = (user) => {
    if (user.id === currentUser.id) return false; // Can't delete yourself
    const targetRole = normalizeRole(user.role);
    if (currentRole === 'owner') return true;
    if (currentRole === 'admin') return targetRole !== 'owner' && targetRole !== 'admin';
    return false;
  };

  const canResetUserPassword = (user) => {
    if (!user || user.id === currentUser.id) return false;
    const targetRole = normalizeRole(user.role);
    if (currentRole === 'owner') return ['admin', 'patternmaker', 'support'].includes(targetRole);
    if (currentRole === 'admin') return ['patternmaker', 'support'].includes(targetRole);
    return false;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetUser) return;

    setError('');
    setSuccess('');

    const newPassword = String(resetPasswordForm.password || '');
    const confirmPassword = String(resetPasswordForm.confirmPassword || '');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Password confirmation does not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/admin/users/${resetUser.id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password: newPassword }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to update password');
      }

      setSuccess(`Password updated for ${resetUser.email}`);
      setResetDialogOpen(false);
      setResetUser(null);
      setResetPasswordForm({ password: '', confirmPassword: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (assignableRoles.length === 0) {
    return (
      <div className="min-h-screen bg-background p-6" data-testid="admin-panel-access-denied">
        <Card className="max-w-xl mx-auto mt-16">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>You do not have permission to access user management.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={onBack} data-testid="admin-panel-access-denied-back-button">
              <ArrowLeft size={14} className="mr-2" /> Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <Button variant="ghost" onClick={onBack} className="mb-4" data-testid="admin-panel-back-button">
          <ArrowLeft size={16} className="mr-2" /> Back to App
        </Button>

        {canManageLegacySnapshots && (
          <Card className="mb-6" data-testid="legacy-snapshot-freeze-card">
            <CardHeader>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-slate-900 flex items-center justify-center text-white">
                    <Camera size={18} />
                  </div>
                  <div>
                    <CardTitle>Legacy Snapshot Freeze</CardTitle>
                    <CardDescription>
                      Audit old patterns without renderable snapshots, then freeze them so future formula changes do not move them on reload.
                    </CardDescription>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={runSnapshotAudit}
                    disabled={snapshotLoading}
                    data-testid="legacy-snapshot-audit-button"
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Audit Missing Snapshots
                  </Button>
                  <Button
                    type="button"
                    onClick={runSnapshotFreeze}
                    disabled={snapshotLoading || !snapshotAudit?.summary?.eligible}
                    data-testid="legacy-snapshot-freeze-button"
                  >
                    <Camera size={16} className="mr-2" />
                    Freeze Missing Snapshots
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <Alert data-testid="legacy-snapshot-freeze-helper-alert">
                <AlertDescription>
                  The freeze action only writes snapshots for supported garments with saved measurements and usable version metadata. Records with existing snapshots are left untouched.
                </AlertDescription>
              </Alert>

              <div className="grid gap-3 md:grid-cols-2" data-testid="legacy-snapshot-scope-panel">
                <div className="space-y-2">
                  <Label htmlFor="legacy-snapshot-client-scope-input">Client scope (optional)</Label>
                  <Input
                    id="legacy-snapshot-client-scope-input"
                    value={snapshotScopeClientNumber}
                    onChange={(event) => setSnapshotScopeClientNumber(event.target.value.toUpperCase())}
                    placeholder="Leave blank for site-wide audit"
                    disabled={snapshotLoading}
                    data-testid="legacy-snapshot-client-scope-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="legacy-snapshot-garment-scope-trigger">Garment scope</Label>
                  <Select value={snapshotScopeGarment} onValueChange={setSnapshotScopeGarment} disabled={snapshotLoading}>
                    <SelectTrigger id="legacy-snapshot-garment-scope-trigger" data-testid="legacy-snapshot-garment-scope-trigger">
                      <SelectValue placeholder="All garments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="legacy-snapshot-garment-scope-option-all">All garments</SelectItem>
                      <SelectItem value="trouser" data-testid="legacy-snapshot-garment-scope-option-trouser">Trouser</SelectItem>
                      <SelectItem value="shirt" data-testid="legacy-snapshot-garment-scope-option-shirt">Shirt</SelectItem>
                      <SelectItem value="jacket" data-testid="legacy-snapshot-garment-scope-option-jacket">Jacket</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(snapshotLoading || snapshotProgress.phase !== 'idle') && (
                <div className="rounded-lg border bg-slate-50 p-4 space-y-2" data-testid="legacy-snapshot-progress-panel">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-slate-900" data-testid="legacy-snapshot-progress-phase-text">
                      {snapshotProgress.phase === 'freeze' ? 'Freezing snapshots' : 'Auditing legacy patterns'}
                    </p>
                    <p className="text-xs text-slate-600" data-testid="legacy-snapshot-progress-count-text">
                      {snapshotProgress.processed} / {snapshotProgress.total || 0}
                    </p>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden" data-testid="legacy-snapshot-progress-bar-track">
                    <div
                      className="h-full bg-slate-900 transition-all"
                      style={{ width: `${snapshotProgress.total ? Math.min(100, (snapshotProgress.processed / snapshotProgress.total) * 100) : 0}%` }}
                      data-testid="legacy-snapshot-progress-bar-fill"
                    />
                  </div>
                  <p className="text-xs text-slate-600" data-testid="legacy-snapshot-progress-current-pattern-text">
                    {snapshotProgress.currentPatternNumber ? `Latest: ${snapshotProgress.currentPatternNumber}` : 'Preparing batch…'}
                  </p>
                </div>
              )}

              {snapshotAudit && (
                <div className="space-y-4" data-testid="legacy-snapshot-audit-results-panel">
                  <div className="grid gap-3 md:grid-cols-4">
                    <div className="rounded-lg border bg-white p-4" data-testid="legacy-snapshot-total-card">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Total</p>
                      <p className="mt-2 text-2xl font-semibold text-slate-900">{snapshotAudit.summary.total}</p>
                    </div>
                    <div className="rounded-lg border bg-emerald-50 p-4" data-testid="legacy-snapshot-eligible-card">
                      <p className="text-xs uppercase tracking-wide text-emerald-700">Eligible</p>
                      <p className="mt-2 text-2xl font-semibold text-emerald-900">{snapshotAudit.summary.eligible || 0}</p>
                    </div>
                    <div className="rounded-lg border bg-sky-50 p-4" data-testid="legacy-snapshot-already-frozen-card">
                      <p className="text-xs uppercase tracking-wide text-sky-700">Already Frozen</p>
                      <p className="mt-2 text-2xl font-semibold text-sky-900">{snapshotAudit.summary['already-frozen'] || 0}</p>
                    </div>
                    <div className="rounded-lg border bg-amber-50 p-4" data-testid="legacy-snapshot-skipped-card">
                      <p className="text-xs uppercase tracking-wide text-amber-700">Skipped</p>
                      <p className="mt-2 text-2xl font-semibold text-amber-900">{snapshotAudit.summary.skipped || 0}</p>
                    </div>
                  </div>

                  <div className="rounded-lg border bg-white" data-testid="legacy-snapshot-garment-summary-table">
                    <div className="border-b px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900">By garment</p>
                    </div>
                    <div className="divide-y">
                      {Object.entries(snapshotAudit.summary.byGarment || {}).map(([garmentKey, garmentSummary]) => (
                        <div key={garmentKey} className="grid grid-cols-4 gap-3 px-4 py-3 text-sm" data-testid={`legacy-snapshot-garment-row-${garmentKey}`}>
                          <p className="font-medium text-slate-900 capitalize">{garmentKey}</p>
                          <p className="text-slate-600">Eligible: {garmentSummary.eligible}</p>
                          <p className="text-slate-600">Frozen: {garmentSummary.alreadyFrozen}</p>
                          <p className="text-slate-600">Skipped: {garmentSummary.skipped}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg border bg-white" data-testid="legacy-snapshot-audit-records-table">
                    <div className="border-b px-4 py-3 flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-900">Latest audit records</p>
                      <div className="text-right">
                        <p className="text-xs text-slate-500" data-testid="legacy-snapshot-audit-generated-at-text">
                          {new Date(snapshotAudit.generatedAt).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500" data-testid="legacy-snapshot-audit-scope-text">
                          Scope: {snapshotAudit.scope?.clientNumber || 'ALL CLIENTS'} · {snapshotAudit.scope?.garmentType === 'all' ? 'ALL GARMENTS' : String(snapshotAudit.scope?.garmentType || '').toUpperCase()}
                        </p>
                      </div>
                    </div>
                    <div className="max-h-72 overflow-auto divide-y">
                      {snapshotAudit.records.slice(0, 120).map((record) => (
                        <div key={record.id} className="grid gap-2 px-4 py-3 md:grid-cols-[1.2fr,0.8fr,0.8fr,0.9fr,1.6fr] text-sm" data-testid={`legacy-snapshot-record-${record.id}`}>
                          <div>
                            <p className="font-medium text-slate-900">{record.patternNumber}</p>
                            <p className="text-xs text-slate-500">{record.clientName || '—'} · {record.clientNumber || '—'}</p>
                          </div>
                          <p className="capitalize text-slate-700">{record.garmentType}</p>
                          <div className="text-xs text-slate-500">
                            <p>{record.geometryVersion || 'No geometry'}</p>
                            <p>{record.formulaVersion || 'No formula'}</p>
                          </div>
                          <div>
                            <Badge
                              variant="outline"
                              className={record.status === 'eligible' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : record.status === 'already-frozen' ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-amber-50 text-amber-700 border-amber-200'}
                              data-testid={`legacy-snapshot-record-status-${record.id}`}
                            >
                              {record.status}
                            </Badge>
                          </div>
                          <p className="text-slate-600">{record.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                  <Users size={20} />
                </div>
                <div>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>Manage users and their permissions</CardDescription>
                </div>
              </div>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <UserPlus size={16} className="mr-2" />
                Create User
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {success && (
              <Alert className="mb-4 border-green-200 bg-green-50">
                <AlertDescription className="text-green-800">{success}</AlertDescription>
              </Alert>
            )}
            
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {loading && <p className="text-center text-muted-foreground py-4">Loading...</p>}
            
            {!loading && users.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No users found</p>
            )}
            
            {!loading && users.length > 0 && (
              <div className="space-y-2">
                {users.map((user) => {
                  const role = normalizeRole(user.role);
                  const RoleIcon = ROLE_ICONS[role] || Users;
                  return (
                    <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg bg-card hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                          <RoleIcon size={18} className="text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">{user.first_name} {user.last_name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                        <Badge variant="outline" className={ROLE_COLORS[role] || ROLE_COLORS.support}>
                          {(ROLE_LABELS[role] || role || 'UNKNOWN').toUpperCase()}
                        </Badge>
                        {user.id === currentUser.id && (
                          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
                            YOU
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {canResetUserPassword(user) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openResetDialog(user)}
                            data-testid={`admin-reset-password-${user.id}`}
                          >
                            <KeyRound size={16} />
                          </Button>
                        )}
                        {canEditUser(user) && (
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(user)} data-testid={`admin-edit-user-${user.id}`}>
                            <Edit size={16} />
                          </Button>
                        )}
                        {canDeleteUser(user) && user.id !== currentUser.id && (
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => openDeleteDialog(user)} data-testid={`admin-delete-user-${user.id}`}>
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Add a new user to the system</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="create-email">Email</Label>
                <Input
                  id="create-email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-first-name">First Name</Label>
                  <Input
                    id="create-first-name"
                    value={createForm.first_name}
                    onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-last-name">Last Name</Label>
                  <Input
                    id="create-last-name"
                    value={createForm.last_name}
                    onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-password">Password</Label>
                <Input
                  id="create-password"
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-role">Role</Label>
                <Select value={createForm.role} onValueChange={(val) => setCreateForm({ ...createForm, role: val })}>
                  <SelectTrigger id="create-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {assignableRoles.map((role) => (
                      <SelectItem key={`create-role-${role}`} value={role}>
                        {role === 'owner' && 'Owner - Full authority'}
                        {role === 'admin' && 'Admin - Manage users/clients/patterns'}
                        {role === 'patternmaker' && 'Patternmaker - Edit/create patterns, no historical deletes'}
                        {role === 'support' && 'Support - View/print/export only'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={loading}>Create User</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information and role</DialogDescription>
          </DialogHeader>
          {editUser && (
            <form onSubmit={handleEditUser}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={editUser.email} disabled className="bg-muted" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-first-name">First Name</Label>
                    <Input
                      id="edit-first-name"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-last-name">Last Name</Label>
                    <Input
                      id="edit-last-name"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-role">Role</Label>
                  <Select
                    value={editForm.role}
                    onValueChange={(val) => setEditForm({ ...editForm, role: val })}
                    disabled={editUser.id === currentUser.id || !canEditUser(editUser)}
                  >
                    <SelectTrigger id="edit-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {assignableRoles.map((role) => (
                        <SelectItem key={`edit-role-${role}`} value={role}>
                          {role === 'owner' && 'Owner - Full authority'}
                          {role === 'admin' && 'Admin - Manage users/clients/patterns'}
                          {role === 'patternmaker' && 'Patternmaker - Edit/create patterns, no historical deletes'}
                          {role === 'support' && 'Support - View/print/export only'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editUser.id === currentUser.id && (
                    <p className="text-xs text-muted-foreground">You cannot change your own role.</p>
                  )}
                  {!canEditUser(editUser) && editUser.id !== currentUser.id && (
                    <p className="text-xs text-muted-foreground">You cannot modify this user&apos;s role.</p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={loading}>Save Changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>Are you sure you want to delete this user? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          {deleteUser && (
            <div className="py-4">
              <p className="text-sm">
                <strong>User:</strong> {deleteUser.first_name} {deleteUser.last_name} ({deleteUser.email})
              </p>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button type="button" variant="destructive" onClick={handleDeleteUser} disabled={loading}>
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent data-testid="admin-reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
            <DialogDescription>
              Set a new password for this user account.
            </DialogDescription>
          </DialogHeader>

          {resetUser && (
            <form onSubmit={handleResetPassword}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>User</Label>
                  <Input value={resetUser.email} disabled className="bg-muted" data-testid="admin-reset-password-user-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-password-new">New Password</Label>
                  <Input
                    id="reset-password-new"
                    type="password"
                    value={resetPasswordForm.password}
                    onChange={(e) => setResetPasswordForm((prev) => ({ ...prev, password: e.target.value }))}
                    required
                    data-testid="admin-reset-password-new-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-password-confirm">Confirm New Password</Label>
                  <Input
                    id="reset-password-confirm"
                    type="password"
                    value={resetPasswordForm.confirmPassword}
                    onChange={(e) => setResetPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                    data-testid="admin-reset-password-confirm-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setResetDialogOpen(false)} data-testid="admin-reset-password-cancel-button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} data-testid="admin-reset-password-submit-button">
                  Update Password
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
